import { lazy, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useQueryState, parseAsInteger, parseAsString } from 'nuqs';
import {
  QrCode,
  Search,
  Plus,
  ExternalLink,
  Trash2,
  Download,
  Copy,
  ScanLine,
  Pencil,
  ChevronLeft,
  ChevronRight,
  Palette,
  Smartphone,
  BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';

// ⚡ Performance Optimization: Lazy load QR code library
const LazyQRCodeCanvas = lazy(() =>
  import('qrcode.react').then((module) => ({
    default: module.QRCodeCanvas,
  }))
);
import { useUIStore } from '@/client/stores/ui-store';
import { formatNumber, formatRelativeTime, copyToClipboard } from '@/client/lib/utils';
import { apiFetch } from '@/client/lib/api';
import { OnboardingEmptyState, SearchEmptyState } from '@/client/components/ui/empty-state';
import { Button } from '@/client/components/ui/button';
import { Card, CardContent } from '@/client/components/ui/card';
import { Badge } from '@/client/components/ui/badge';
import { Skeleton } from '@/client/components/ui/skeleton';
import { Input } from '@/client/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/client/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/client/components/ui/table';

// Shape types for QR code customization
type BodyShape = 'square' | 'rounded' | 'circle';
type EyeFrameShape = 'square' | 'rounded';
type EyeBallShape = 'square' | 'rounded' | 'circle';

// Type for QR code data from API
interface QRCodeData {
  id: string;
  name: string;
  description?: string | null;
  targetUrl: string;
  linkId?: string | null;
  projectId: string;
  workspaceId?: string;
  styleOptions?: {
    size?: number;
    margin?: number;
    errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
    darkColor?: string;
    lightColor?: string;
    bodyShape?: BodyShape;
    eyeFrameShape?: EyeFrameShape;
    eyeBallShape?: EyeBallShape;
  };
  logoOptions?: {
    type: 'default' | 'custom' | 'none';
    customUrl?: string;
    size?: number;
  };
  totalScans: number;
  createdAt: string;
  updatedAt: string;
}

// Helper to truncate long URLs for display
function truncateUrl(url: string, maxLength = 40) {
  if (url.length <= maxLength) return url;
  return `${url.substring(0, maxLength - 3)}…`;
}

export function QRCodesPage() {
  const navigate = useNavigate();
  const { openDeleteConfirmation } = useUIStore();

  // URL-synced state
  const [searchQuery, setSearchQuery] = useQueryState('q', parseAsString.withDefault(''));
  const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1));

  // Fetch QR codes
  const { data, isLoading, error } = useQuery({
    queryKey: ['qr-codes', { searchQuery, page }],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: '20',
        offset: ((page - 1) * 20).toString(),
      });
      if (searchQuery) params.set('search', searchQuery);

      const response = await apiFetch(`/api/qr-codes?${params}`);
      if (!response.ok) {
        throw new Error('Failed to fetch QR codes');
      }
      return response.json();
    },
  });

  const handleDelete = (qrCode: QRCodeData) => {
    openDeleteConfirmation('qr-code', qrCode.id, qrCode.name);
  };

  const handleCopyUrl = async (url: string, e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    await copyToClipboard(url);
    toast.success('URL copied to clipboard!');
  };

  const handleDownload = (qrCode: QRCodeData, e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    const canvas = document.getElementById(`qr-canvas-${qrCode.id}`) as HTMLCanvasElement;
    if (!canvas) return;

    const link = document.createElement('a');
    link.download = `qr-${qrCode.name.toLowerCase().replace(/\s+/g, '-')}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    toast.success('QR code downloaded!');
  };

  // Calculate stats
  const totalScans =
    data?.data?.reduce((sum: number, qr: QRCodeData) => sum + qr.totalScans, 0) ?? 0;
  const totalQRCodes = data?.total ?? 0;
  const totalPages = Math.ceil(totalQRCodes / 20);

  return (
    <div className="space-y-6 stagger-children relative">
      {/* Decorative background gradient */}
      <div className="absolute inset-0 overflow-hidden opacity-30 pointer-events-none">
        <div className="absolute -top-1/2 -right-1/4 w-[800px] h-[800px] rounded-full bg-gradient-to-br from-indigo-500/20 via-cyan-500/10 to-transparent blur-3xl" />
        <div className="absolute -bottom-1/2 -left-1/4 w-[600px] h-[600px] rounded-full bg-gradient-to-tr from-cyan-500/20 via-indigo-500/10 to-transparent blur-3xl" />
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 relative">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
            <div className="size-10 rounded-xl bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-indigo-500/20">
              <QrCode className="size-5 text-white" />
            </div>
            QR Codes
          </h1>
          <p className="text-muted-foreground mt-2 text-sm sm:text-base">
            Generate scannable codes for any URL
          </p>
        </div>
        <Button
          onClick={() => navigate('/qr-codes/new')}
          size="lg"
          className="rounded-xl glow gap-2 bg-gradient-to-r from-indigo-500 to-cyan-500 hover:from-indigo-600 hover:to-cyan-600"
        >
          <Plus className="size-5" />
          Create QR Code
        </Button>
      </div>

      {/* Quick Stats */}
      {totalQRCodes > 0 && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-indigo-50 to-cyan-50 dark:from-indigo-950/30 dark:to-cyan-950/30">
            <CardContent className="flex items-center gap-4 p-4">
              <div className="size-12 rounded-xl bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                <QrCode className="size-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground font-medium">Total QR Codes</p>
                <p className="text-2xl font-bold tabular-nums">{formatNumber(totalQRCodes)}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30">
            <CardContent className="flex items-center gap-4 p-4">
              <div className="size-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/20">
                <ScanLine className="size-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground font-medium">Total Scans</p>
                <p className="text-2xl font-bold tabular-nums">{formatNumber(totalScans)}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30">
            <CardContent className="flex items-center gap-4 p-4">
              <div className="size-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                <BarChart3 className="size-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground font-medium">Avg. Scans</p>
                <p className="text-2xl font-bold tabular-nums">
                  {totalQRCodes > 0 ? formatNumber(Math.round(totalScans / totalQRCodes)) : '0'}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 size-5 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Search QR codes by name or URL…"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(1);
          }}
          className="pl-12 h-12 rounded-xl"
        />
      </div>

      {/* QR Codes Table */}
      <Card className="rounded-2xl overflow-hidden py-0">
        {error ? (
          <CardContent className="p-12 text-center">
            <div className="size-24 rounded-3xl bg-destructive/10 flex items-center justify-center mx-auto mb-6">
              <QrCode className="size-12 text-destructive/50" />
            </div>
            <h3 className="text-xl font-semibold mb-2 text-destructive">Error Loading QR Codes</h3>
            <p className="text-muted-foreground">{error.message}</p>
          </CardContent>
        ) : isLoading ? (
          <CardContent className="p-6 space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 p-4">
                <Skeleton className="size-14 rounded-xl" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-5 w-1/3" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
                <Skeleton className="w-20 h-8 rounded-lg" />
              </div>
            ))}
          </CardContent>
        ) : data?.data?.length === 0 ? (
          searchQuery ? (
            <SearchEmptyState
              searchQuery={searchQuery}
              onClear={() => setSearchQuery('')}
              entityName="QR codes"
            />
          ) : (
            <OnboardingEmptyState
              icon={QrCode}
              title="Create your first QR code"
              steps={[
                { icon: QrCode, text: 'Generate a QR code for any URL' },
                { icon: Palette, text: 'Customize colors and add your logo' },
                { icon: Smartphone, text: 'Download and share anywhere' },
              ]}
              action={{
                label: 'Create your first QR code',
                onClick: () => navigate('/qr-codes/new'),
                icon: Plus,
              }}
            />
          )
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block">
              <Table>
                <TableHeader className="bg-muted/50">
                  <TableRow>
                    <TableHead className="px-4 w-16">QR</TableHead>
                    <TableHead className="px-4">Name & URL</TableHead>
                    <TableHead className="px-4">Scans</TableHead>
                    <TableHead className="px-4">Created</TableHead>
                    <TableHead className="px-4 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.data?.map((qrCode: QRCodeData) => {
                    const fgColor = qrCode.styleOptions?.darkColor || '#000000';
                    const bgColor = qrCode.styleOptions?.lightColor || '#ffffff';
                    const showLogo = qrCode.logoOptions?.type !== 'none';

                    return (
                      <TableRow key={qrCode.id} className="group">
                        <TableCell className="px-4 py-3">
                          <div className="size-12 rounded-lg border bg-white flex items-center justify-center overflow-hidden">
                            <Suspense
                              fallback={<div className="size-10 bg-muted animate-pulse rounded" />}
                            >
                              <LazyQRCodeCanvas
                                id={`qr-canvas-${qrCode.id}`}
                                value={qrCode.targetUrl}
                                size={40}
                                level={showLogo ? 'H' : 'M'}
                                marginSize={0}
                                fgColor={fgColor}
                                bgColor={bgColor}
                                imageSettings={
                                  showLogo && qrCode.logoOptions?.type === 'default'
                                    ? {
                                        src: '/assets/logo.png',
                                        height: 8,
                                        width: 8,
                                        excavate: true,
                                      }
                                    : undefined
                                }
                              />
                            </Suspense>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3 max-w-md">
                          <div className="space-y-1">
                            <Link
                              to={`/qr-codes/${qrCode.id}`}
                              className="font-medium text-sm hover:text-primary hover:underline block truncate"
                            >
                              {qrCode.name}
                            </Link>
                            {qrCode.description && (
                              <p className="text-xs text-muted-foreground truncate">
                                {qrCode.description}
                              </p>
                            )}
                            <a
                              href={qrCode.targetUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-muted-foreground hover:text-foreground truncate block font-mono"
                              title={qrCode.targetUrl}
                            >
                              {truncateUrl(qrCode.targetUrl, 50)}
                            </a>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <Badge
                            variant="secondary"
                            className="gap-1 bg-purple-500/10 text-purple-600 border-purple-500/20"
                          >
                            <ScanLine className="size-3" />
                            {formatNumber(qrCode.totalScans)}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-4 py-3 text-sm text-muted-foreground">
                          {formatRelativeTime(qrCode.createdAt)}
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  onClick={() => navigate(`/qr-codes/${qrCode.id}/edit`)}
                                >
                                  <Pencil className="size-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Edit</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  onClick={(e) => handleDownload(qrCode, e)}
                                >
                                  <Download className="size-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Download PNG</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  onClick={(e) => handleCopyUrl(qrCode.targetUrl, e)}
                                >
                                  <Copy className="size-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Copy URL</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon-sm" asChild>
                                  <a
                                    href={qrCode.targetUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <ExternalLink className="size-4" />
                                  </a>
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Open URL</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon-sm" asChild>
                                  <Link to={`/qr-codes/${qrCode.id}`}>
                                    <BarChart3 className="size-4" />
                                  </Link>
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>View analytics</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  onClick={() => handleDelete(qrCode)}
                                  className="hover:bg-destructive/10 hover:text-destructive"
                                >
                                  <Trash2 className="size-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Delete</TooltipContent>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-border">
              {data?.data?.map((qrCode: QRCodeData) => {
                const fgColor = qrCode.styleOptions?.darkColor || '#000000';
                const bgColor = qrCode.styleOptions?.lightColor || '#ffffff';
                const showLogo = qrCode.logoOptions?.type !== 'none';

                return (
                  <Link
                    key={qrCode.id}
                    to={`/qr-codes/${qrCode.id}`}
                    className="flex items-start gap-4 p-4 hover:bg-muted/30 transition-colors"
                  >
                    <div className="size-14 rounded-lg border bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
                      <Suspense
                        fallback={<div className="size-12 bg-muted animate-pulse rounded" />}
                      >
                        <LazyQRCodeCanvas
                          id={`qr-canvas-mobile-${qrCode.id}`}
                          value={qrCode.targetUrl}
                          size={48}
                          level={showLogo ? 'H' : 'M'}
                          marginSize={0}
                          fgColor={fgColor}
                          bgColor={bgColor}
                          imageSettings={
                            showLogo && qrCode.logoOptions?.type === 'default'
                              ? {
                                  src: '/assets/logo.png',
                                  height: 10,
                                  width: 10,
                                  excavate: true,
                                }
                              : undefined
                          }
                        />
                      </Suspense>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{qrCode.name}</p>
                      <p className="text-xs text-muted-foreground truncate font-mono">
                        {truncateUrl(qrCode.targetUrl, 35)}
                      </p>
                      <div className="flex items-center gap-3 mt-2">
                        <Badge
                          variant="secondary"
                          className="gap-1 bg-purple-500/10 text-purple-600 border-purple-500/20 text-[10px]"
                        >
                          <ScanLine className="size-2.5" />
                          {formatNumber(qrCode.totalScans)} scans
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {formatRelativeTime(qrCode.createdAt)}
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between py-4 px-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Showing <span className="font-medium text-foreground">{(page - 1) * 20 + 1}</span>{' '}
                  to{' '}
                  <span className="font-medium text-foreground">
                    {Math.min(page * 20, totalQRCodes)}
                  </span>{' '}
                  of <span className="font-medium text-foreground">{totalQRCodes}</span> QR codes
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="rounded-lg"
                  >
                    <ChevronLeft className="size-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                    className="rounded-lg"
                  >
                    Next
                    <ChevronRight className="size-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
